import Big from 'big.js'

import type { InvoiceListItem } from '$lib/stores/invoices/types'
import type {
	CalculatedInvoice,
	ClientListItem,
	EstimateListItem,
	Payment
} from '$lib/types/client_types'
import type {
	ServerClientListItem,
	ServerEstimateListItem,
	ServerInvoice,
	ServerInvoiceListItem,
	ServerPayment
} from '$lib/types/server_types'

import type { CalculatedDocumentItem, DocumentItemTaxTotal, ServerDocumentItem } from './components/document/doc_types'
import type { DocShippingItem, ServerDocShippingItem } from './stores/doc_shipping_items/types'
import { CalculationType } from './types'



export function parseDocItem(item: ServerDocumentItem): CalculatedDocumentItem {

	const tax_totals: DocumentItemTaxTotal = {}
	Object.keys(item.tax_totals).forEach((key) => {
		tax_totals[key] = parseDecimal(item.tax_totals[key])
	})
	return {
		...item,
		price: parseDecimal(item.price),
		quantity: parseDecimal(item.quantity),
		tax: parseDecimal(item.tax),
		tax_totals,
		subtotal: parseDecimal(item.subtotal),
		total: parseDecimal(item.total),
	}
}

export function parseDocShippingItem(item: ServerDocShippingItem): DocShippingItem {

	const tax_totals: DocumentItemTaxTotal = {}
	Object.keys(item.tax_totals).forEach((key) => {
		tax_totals[key] = parseDecimal(item.tax_totals[key])
	})

	return {
		...item,
		price: parseDecimal(item.price),
		subtotal: parseDecimal(item.subtotal),
		tax: parseDecimal(item.tax),
		tax_totals,
		total: parseDecimal(item.total)
	}
}


export function parseInvoice(invoice: ServerInvoice): CalculatedInvoice {
	// TODO replace with io-ts
	for (const discount of invoice.pre_tax_discounts) {
		if (discount.type !== CalculationType.Fixed && discount.type !== CalculationType.Rate) {
			throw new Error(`Invalid pre_tax_discount type ${discount.type}`)
		}
	}
	for (const discount of invoice.post_tax_discounts) {
		if (discount.type !== CalculationType.Fixed && discount.type !== CalculationType.Rate) {
			throw new Error(`Invalid pre_tax_discount type ${discount.type}`)
		}
	}
	for (const deposit of invoice.deposits) {
		if (deposit.type !== CalculationType.Fixed && deposit.type !== CalculationType.Rate) {
			throw new Error(`Invalid pre_tax_discount type ${deposit.type}`)
		}
	}

	const calcInvoice = {
		...invoice,
		items: invoice.items.map(parseDocItem),
		items_delete: [],
		shipping_items: invoice.shipping_items.map(parseDocShippingItem),
		shipping_items_delete: [],
		pre_tax_discounts: invoice.pre_tax_discounts.map((discount) => ({
			...discount,
			rate: parseDecimal(discount.rate),
			fixed: parseDecimal(discount.fixed),
			total: parseDecimal(discount.total)
		})),
		taxes: invoice.taxes.map((tax) => ({
			...tax,
			rate: parseDecimal(tax.rate),
			total: parseDecimal(tax.total)
		})),
		post_tax_discounts: invoice.post_tax_discounts.map((discount) => ({
			...discount,
			rate: parseDecimal(discount.rate),
			fixed: parseDecimal(discount.fixed),
			total: parseDecimal(discount.total)
		})),
		deposits: invoice.deposits.map((deposit) => ({
			...deposit,
			rate: parseDecimal(deposit.rate),
			fixed: parseDecimal(deposit.fixed),
			total: parseDecimal(deposit.total)
		})),
		balance: parseDecimal(invoice.balance),
		deposit_due: parseDecimal(invoice.deposit_due),
		deposit_total: parseDecimal(invoice.deposit_total),
		grand_total: parseDecimal(invoice.grand_total),
		payment_total: parseDecimal(invoice.payment_total),
		post_tax_total: parseDecimal(invoice.post_tax_total),
		pre_tax_total: parseDecimal(invoice.pre_tax_total),
		subtotal: parseDecimal(invoice.subtotal),
		has_attachments: invoice.has_attachments
	}
	
	return calcInvoice
}

/**
 * Cleans up a string of any length/undefined/null/negative, and returns a non-negative Big decimal.
 *
 */
export function parseDecimal(text: string, min: number | null = null, max: number | null = null) {
	if (!text) return Big(0)
	const negative = text.trim().startsWith('-')
	text = text.replace(/[^\d^\.]/g, '')
	const [left, ...rest] = text.split('.')
	let value = Big((left || 0) + '.' + rest.join(''))
	if (negative) {
		value = value.times(-1)
	}
	if (min !== null && value.lt(min)) {
		value = Big(min)
	}
	if (max !== null && value.gt(max)) {
		value = Big(max)
	}
	return value
}

export function parsePayment(payment: ServerPayment): Payment {
	return {
		...payment,
		total: parseDecimal(payment.total)
	}
}

export function parsePayments(payments: ServerPayment[]): Payment[] {
	return payments.map(parsePayment)
}

export function parseInvoiceList(invoices: ServerInvoiceListItem[]): InvoiceListItem[] {
	return invoices.map((invoice) => ({
		...invoice,
		balance: parseDecimal(invoice.balance),
		deposit_due: parseDecimal(invoice.deposit_due),
		payment: parseDecimal(invoice.payment)
	}))
}

export function parseEstimateList(estimates: ServerEstimateListItem[]): EstimateListItem[] {
	return estimates.map((estimate) => ({
		...estimate,
		subtotal: parseDecimal(estimate.subtotal),
		discount: parseDecimal(estimate.discount),
		tax: parseDecimal(estimate.tax),
		grandtotal: parseDecimal(estimate.grand_total)
	}))
}

export function parseClientList(clients: ServerClientListItem[]): ClientListItem[] {
	return clients.map((client) => {
		const cs = JSON.parse(client.currencies_summary)

		//console.log("clients: ", cs)

		const cs1 = cs.map((cis: any) => {
			return {
				currency_id: cis.currency_id,
				count: cis.count,
				grand_total: parseDecimal(cis.grand_total.toString()),
				paid: parseDecimal(cis.paid.toString()),
				balance: parseDecimal(cis.balance.toString()),
				overdue: parseDecimal(cis.overdue.toString())
			}
		})

		//console.log("client.currencies_summary: ", cs1)

		return {
			...client,
			currencies_summary: cs1

			/*		grand_total: parseDecimal(client.grand_total),
		paid: parseDecimal(client.paid),
		owing: parseDecimal(client.owing)
		*/
		}
	})
}
